import type { FieldSelection, GlobalCollectionId, GlobalFieldId } from '#layers/data-model/public';

export interface FilterByPermissionsOptions {
	collectionId: GlobalCollectionId;
	selection: FieldSelection;
	canReadField: (collectionId: GlobalCollectionId, fieldId: GlobalFieldId) => boolean;
	canRead: (collectionId: GlobalCollectionId) => boolean;
	isAdmin: boolean;
	resolveField: (collectionId: GlobalCollectionId, apiName: string) => {
		id: GlobalFieldId;
		isRelation: boolean;
		oppositeCollectionId?: GlobalCollectionId;
		isStudioOnly?: boolean;
	} | undefined;
}

export interface FilterByPermissionsResult {
	filtered: FieldSelection;
	excluded: string[];
}

export function filterByPermissions(options: FilterByPermissionsOptions): FilterByPermissionsResult {
	return filterAtLevel(options.collectionId, options.selection, options);
}

function filterAtLevel(
	collectionId: GlobalCollectionId,
	selection: FieldSelection,
	options: FilterByPermissionsOptions,
): FilterByPermissionsResult {
	const filtered: FieldSelection = { ...selection, fields: [] };
	const excluded: string[] = [];

	for (const name of selection.fields ?? ['*']) {
		const field = name === '*' ? undefined : options.resolveField(collectionId, name);
		if (field?.isStudioOnly) continue;

		if (!field || options.isAdmin || options.canReadField(collectionId, field.id)) {
			filtered.fields!.push(name);
		}
		else {
			excluded.push(name);
		}
	}

	if (selection.include) {
		filtered.include = {};

		for (const [name, query] of Object.entries(selection.include)) {
			const field = options.resolveField(collectionId, name);
			if (field?.isStudioOnly) continue;

			if (
				field
				&& !options.isAdmin
				&& (
					!options.canReadField(collectionId, field.id)
					|| (field.oppositeCollectionId && !options.canRead(field.oppositeCollectionId))
				)
			) {
				excluded.push(name);
				continue;
			}

			if (!field?.isRelation || !field.oppositeCollectionId) {
				filtered.include[name] = query;
				continue;
			}

			const nested = filterAtLevel(field.oppositeCollectionId, query, options);
			filtered.include[name] = nested.filtered;
			excluded.push(...nested.excluded.map((path) => `${name}.${path}`));
		}
	}

	return { filtered, excluded };
}
